import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloudServerOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { api, AUTH_ROLE_KEY } from "../services/apiClient";
import { canUseUserFilter, fetchUserOptions, userQuery } from "../utils/adminUserFilter";

const { Title, Text } = Typography;

const SERVICE_CATEGORY = {
  PROMPT: { label: "Prompt", color: "purple" },
  VIDEO: { label: "Video", color: "blue" },
  AUTO_UPLOAD: { label: "Auto Upload", color: "green" },
  SYSTEM: { label: "Hệ thống", color: "gold" },
};

const VIDEO_STATUS = {
  PENDING_DOWNLOAD: { label: "Chờ tải", color: "#8c8c8c" },
  DOWNLOADING: { label: "Đang tải", color: "#1677ff" },
  DOWNLOADED: { label: "Đã tải", color: "#13c2c2" },
  RENDERING: { label: "Đang render", color: "#722ed1" },
  READY_TO_UPLOAD: { label: "Chờ upload", color: "#fa8c16" },
  UPLOADING: { label: "Đang upload", color: "#2f54eb" },
  COMPLETED: { label: "Đã upload", color: "#52c41a" },
  FAILED: { label: "Lỗi xử lý", color: "#f5222d" },
  DOWNLOAD_FAILED: { label: "Lỗi tải", color: "#cf1322" },
};

const number = (value) => Number(value || 0).toLocaleString("vi-VN");

function KpiCard({ color, icon, label, value, note }) {
  return (
    <Card bordered={false} className="dashboard-kpi-card">
      <div className="dashboard-kpi-content">
        <div>
          <Text className="dashboard-kpi-label">{label}</Text>
          <Title level={3}>{number(value)}</Title>
          {note ? <Text className="dashboard-kpi-note" type="secondary">{note}</Text> : null}
        </div>
        <span className="dashboard-kpi-icon" style={{ background: color }}>{icon}</span>
      </div>
    </Card>
  );
}

export default function Home() {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const role = localStorage.getItem(AUTH_ROLE_KEY) || "USER";
  const showUserFilter = canUseUserFilter();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDashboard(await api.get(`/api/dashboard${userQuery(selectedUserId)}`));
    } catch (requestError) {
      setError(requestError.message || "Không thể tải dữ liệu Dashboard");
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showUserFilter) return;
    fetchUserOptions()
      .then(setUsers)
      .catch((requestError) => setError(requestError.message));
  }, [showUserFilter]);

  const totals = useMemo(() => dashboard?.totals || {}, [dashboard]);
  const isAllScope = dashboard?.scope === "ALL";
  const kpis = useMemo(() => {
    const first = isAllScope
      ? {
          label: "Người dùng hoạt động",
          value: totals.activeUsers,
          note: `${number(totals.users)} tài khoản trong phạm vi`,
          icon: <TeamOutlined />,
          color: "#722ed1",
        }
      : {
          label: "Dịch vụ đang dùng",
          value: totals.services,
          note: "Tổng tài nguyên đã cấu hình",
          icon: <AppstoreOutlined />,
          color: "#722ed1",
        };
    return [
      first,
      {
        label: isAllScope ? "Tổng dịch vụ" : "Kênh đa nền tảng",
        value: isAllScope ? totals.services : totals.channels,
        note: isAllScope ? "Trên tất cả module" : "YouTube, TikTok, Facebook",
        icon: isAllScope ? <AppstoreOutlined /> : <YoutubeOutlined />,
        color: "#1677ff",
      },
      {
        label: isAllScope ? "Kênh đa nền tảng" : "Cấu hình Auto Upload",
        value: isAllScope ? totals.channels : totals.configs,
        note: isAllScope ? "YouTube, TikTok, Facebook" : "Cấu hình đang quản lý",
        icon: isAllScope ? <YoutubeOutlined /> : <SettingOutlined />,
        color: "#13c2c2",
      },
      {
        label: isAllScope ? "Cấu hình Auto Upload" : "Video xử lý",
        value: isAllScope ? totals.configs : totals.videos,
        note: isAllScope ? "Cấu hình đang quản lý" : `${number(totals.failedVideos)} video lỗi`,
        icon: isAllScope ? <SettingOutlined /> : <VideoCameraOutlined />,
        color: "#fa8c16",
      },
      {
        label: isAllScope ? "Video xử lý" : "Đã upload",
        value: isAllScope ? totals.videos : totals.completedVideos,
        note: `${number(totals.completedVideos)} hoàn tất · ${number(totals.failedVideos)} lỗi`,
        icon: isAllScope ? <VideoCameraOutlined /> : <CheckCircleOutlined />,
        color: "#52c41a",
      },
      {
        label: "Worker hoạt động",
        value: totals.activeWorkers,
        note: "Worker Agent trạng thái ACTIVE",
        icon: <CloudServerOutlined />,
        color: "#2f54eb",
      },
    ];
  }, [isAllScope, totals]);

  const serviceTotal = (dashboard?.services || []).reduce((sum, item) => sum + Number(item.count || 0), 0);
  const serviceColumns = [
    {
      title: "DỊCH VỤ",
      dataIndex: "label",
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: "NHÓM",
      dataIndex: "category",
      width: 130,
      render: (value) => {
        const category = SERVICE_CATEGORY[value] || { label: value, color: "default" };
        return <Tag color={category.color}>{category.label}</Tag>;
      },
    },
    {
      title: "SỐ LƯỢNG",
      dataIndex: "count",
      width: 110,
      align: "right",
      render: number,
    },
    {
      title: "TỶ TRỌNG",
      width: "34%",
      render: (_, item) => (
        <Progress
          percent={serviceTotal ? Math.round((Number(item.count || 0) / serviceTotal) * 100) : 0}
          size="small"
          strokeColor="#1677ff"
        />
      ),
    },
  ];

  const userColumns = [
    {
      title: "USER",
      render: (_, item) => (
        <div>
          <Text strong>{item.username || "-"}</Text>
          <br />
          <Text type="secondary">{item.email || "-"}</Text>
        </div>
      ),
    },
    {
      title: "ROLE",
      dataIndex: "role",
      width: 120,
      render: (value) => <Tag color={value === "SUPER_ADMIN" ? "purple" : value === "ADMIN" ? "blue" : "default"}>{value || "USER"}</Tag>,
    },
    { title: "DỊCH VỤ", dataIndex: "serviceCount", width: 100, align: "right", render: number },
    { title: "KÊNH", dataIndex: "channelCount", width: 90, align: "right", render: number },
    { title: "CẤU HÌNH", dataIndex: "configCount", width: 100, align: "right", render: number },
    { title: "VIDEO", dataIndex: "videoCount", width: 90, align: "right", render: number },
    {
      title: "KẾT QUẢ",
      width: 160,
      render: (_, item) => (
        <div className="dashboard-result-cell">
          <Text type="success">{number(item.completedVideoCount)} hoàn tất</Text>
          <Text type="danger">{number(item.failedVideoCount)} lỗi</Text>
        </div>
      ),
    },
    { title: "WORKER", dataIndex: "activeWorkerCount", width: 90, align: "right", render: number },
  ];

  return (
    <div className="dashboard-page">
      <div className="page-heading">
        <div>
          <Title level={3}>Dashboard</Title>
          <Text type="secondary">
            {isAllScope
              ? "Tổng quan dữ liệu tất cả người dùng và dịch vụ."
              : "Tổng quan dữ liệu theo người dùng và dịch vụ."}
          </Text>
        </div>
      </div>

      <div className="dashboard-toolbar">
        {showUserFilter ? (
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Tất cả người dùng"
            value={selectedUserId}
            onChange={setSelectedUserId}
            options={users}
          />
        ) : (
          <Tag color="blue">Phạm vi: dữ liệu của bạn</Tag>
        )}
        <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Làm mới</Button>
        <Text type="secondary" className="dashboard-role-note">
          Quyền: {role} · {isAllScope ? "Đang xem tổng hệ thống" : "Đang xem theo user"}
        </Text>
      </div>

      {error ? <Alert className="page-alert" message={error} type="error" showIcon /> : null}

      <Spin spinning={loading && !dashboard}>
        <Row gutter={[16, 16]} className="dashboard-kpi-row">
          {kpis.map((item) => (
            <Col xs={24} sm={12} lg={8} xl={4} key={item.label}>
              <KpiCard {...item} />
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} className="dashboard-main-row">
          <Col xs={24} xl={15}>
            <Card className="criclebox dashboard-section-card" title="Thống kê theo dịch vụ">
              <Table
                columns={serviceColumns}
                dataSource={dashboard?.services || []}
                pagination={false}
                rowKey="code"
                size="small"
              />
            </Card>
          </Col>
          <Col xs={24} xl={9}>
            <Card className="criclebox dashboard-section-card" title="Trạng thái video">
              <div className="dashboard-status-list">
                {Object.entries(VIDEO_STATUS).map(([code, meta]) => {
                  const value = dashboard?.videoStatuses?.[code] || 0;
                  const percent = totals.videos ? Math.round((Number(value) / Number(totals.videos)) * 100) : 0;
                  return (
                    <div className="dashboard-status-item" key={code}>
                      <div className="dashboard-status-copy">
                        <span className="dashboard-status-dot" style={{ background: meta.color }} />
                        <Text>{meta.label}</Text>
                        <Text strong>{number(value)}</Text>
                      </div>
                      <Progress percent={percent} showInfo={false} strokeColor={meta.color} size="small" />
                    </div>
                  );
                })}
              </div>
            </Card>
          </Col>
        </Row>

        <Card
          className="criclebox dashboard-section-card"
          title={isAllScope ? "Thống kê theo người dùng" : "Thống kê tài khoản"}
        >
          <Table
            columns={userColumns}
            dataSource={dashboard?.users || []}
            pagination={false}
            rowKey="userId"
            scroll={{ x: 980 }}
            size="small"
          />
        </Card>
      </Spin>
    </div>
  );
}
